import { getProperties } from '@ember/object';

import { isBoxCoords, isLineCoords, isPointCoords, isPolygonCoords } from 'rise-ui/utils/coords.js';
import config from 'rise-ui/config/config.js';
import { formatDate, utcOffset } from 'rise-ui/utils/date.js';
import { isSet } from 'rise-ui/utils/nullish.js';
import { get } from 'rise-ui/utils/object.js';
import { toArray } from 'rise-ui/utils/array.js';

const { data: {
  creator, provider,
  location: { id: locationIdTpl }
} } = config;

const context = {
  '@context': [{
    '@vocab': 'https://schema.org/',
    gsp: 'http://www.opengis.net/ont/geosparql#',
    hyf: 'https://www.opengis.net/def/schema/hy_features/hyf/',
    ts_spacing: 'https://odm2.github.io/ODM2/schemas/ODM2_Current/columns/ODM2Results_TimeSeriesResults_IntendedTimeSpacing',
  }],
};

export function buildJsonLd(data) {
  return JSON.stringify({
      ...context,
      '@type': undefined,
      provider,
      creator,
      isAccessibleForFree: true,
      ...data,
    }, null, 2);
}

export function minDataset(url = null) {
  return url ? { '@id': url } : {};
}

export function baseDataset(name, description, url = null, data = {}) {
  return {
    ...minDataset(url),
    ...(url ? { url } : {}),
    '@type': 'Dataset',
    name: name,
    description: description,
    ...(data || {}),
  };
}

export function minItemDataset(item, urlFn = null) {
  const { id, title,  catalogRecord }
    = getProperties(item, [ 'id', 'title', 'catalogRecord' ]);
  const { id: recordId } = getProperties(catalogRecord, [ 'id' ]);
  return minDataset(urlFn?.('catalog.record.item', recordId, id));
}

export function baseItemDataset(item, urlFn = null, data = {}) {
  const {
    id, title, description, catalogRecord,
    parameterName, parameterUnit, parameterTimestep,
    temporalStartDate: start, temporalEndDate: end, isTimeSeries,
  } = getProperties(item, [
    'id', 'title', 'description', 'catalogRecord',
    'parameterName', 'parameterUnit', 'parameterTimestep',
    'temporalStartDate', 'temporalEndDate', 'isTimeSeries',
  ]);
  const { id: recordId } = getProperties(catalogRecord, [ 'id' ]);
  return baseDataset(title, description, urlFn?.('catalog.record.item', recordId, id), {
    ...(!isTimeSeries ? {} : {
      'variableMeasured': parameterName,
      temporalCoverage: `${date(start)}/${date(end)}`,
      unitOfMeasurement: parameterUnit,
      ts_spacing: parameterTimestep,
    }),
    ...(data || {}),
  });
}

export function itemDataset(item, urlFn = null, data = {}) {
  const { catalogRecord } = getProperties(item, [ 'description', 'catalogRecord' ]);
  const { location, tags } = getProperties(catalogRecord, [ 'location', 'tags' ]);
  return baseItemDataset(item, urlFn, {
    about: minLocationDataset(location, urlFn),
    isPartOf: minCatalogRecordDataset(catalogRecord, urlFn),
    ...(tags?.length ? { keywords: tags } : {}),
    ...spatialCoverage(location),
    ...(data || {}),
  });
}

export function minCatalogRecordDataset(record, urlFn = null) {
  const { id, title } = getProperties(record, [ 'id', 'title' ]);
  return minDataset(urlFn?.('catalog.record', id));
}

export function baseCatalogRecordDataset(record, urlFn = null, data = {}) {
  const { id, title, description } = getProperties(record, [ 'id', 'title', 'description' ]);
  return baseDataset(title, description, urlFn?.('catalog.record', id), data);
}

export function catalogRecordDataset(record, urlFn = null, data = {}) {
  const { tags, location } = record;
  return baseCatalogRecordDataset(record, urlFn, {
    ...(tags?.length ? { keywords: tags } : {}),
    ...spatialCoverage(location),
    about: minLocationDataset(location, urlFn),
    hasPart: toArray(record.items).map(item => minItemDataset(item, urlFn)),
    ...(data || {}),
  });
}

export function minLocationDataset(location) {
  return minDataset(locationIdTpl.replace('%s', get(location, 'id')));
}

export function baseLocationDataset(location, urlFn = null, data = {}) {
  const { id, name, description, type }
    = getProperties(location, [ 'id', 'name', 'description', 'type' ]);
  return baseDataset(name, description, locationIdTpl.replace('%s', id), {
    '@type': [
      'Place',
      'hyf:HY_HydrometricFeature',
      'hyf:HY_HydroLocation',
    ],
    'hyf:HY_HydroLocationType': type,
    ...(data || {}),
  });
}

export function locationDataset(location, urlFn = null, data = {}) {
  const { coordinates } = location;
  return baseLocationDataset(location, urlFn, {
    'description': location.description,
    'geo': coordsToGeo(coordinates),
    'gsp:asWKT': coordsToWkt(coordinates),
    geoJSON: coordinates,
    subjectOf: toArray(location.catalogRecords).reduce((data, record) => {
      record.items.forEach(item => data.push(minItemDataset(item, urlFn)));
      return data;
    }, []),
    ...(data || {}),
  });
}

export function coordsArrayToGeo(coordsArray) {
  return coordsArray.map(([ lng, lat ]) => `${lat} ${lng}`).join(' ');
}

export function coordsArrayToGeoBox(coordsArray) {
  let lowerLeft; // The first point is the lower corner,
  let topRight; // The first point is the lower corner,
  coordsArray.forEach(([ lng, lat ]) => {
    if (!lowerLeft || lat < lowerLeft[0] || lng > lowerLeft[1]) {
      lowerLeft = [ lat, lng ];
    }
    if (!topRight || lat > topRight[0] || lng < topRight[1]) {
      topRight = [ lat, lng ];
    }
  });
  return [ ...lowerLeft, ...topRight ].join(' ');
}

export function coordsToGeo(coords, elevation = null) {
  let ca = coords?.coordinates;
  if (!ca) {
    return;
  }
  let geo;
  if (isPointCoords(coords)) {
    geo = { '@type': 'GeoCoordinates', latitude: ca[1], longitude: ca[0] };
  } else {
    geo = { '@type': 'GeoShape' };
    if (isLineCoords(coords)) {
      geo['line'] = coordsArrayToGeo(ca);
    } else {
      ca = ca?.[0];
      if (isBoxCoords(coords)) {
        geo['box'] = coordsArrayToGeoBox(ca);
      } else if (isPolygonCoords(coords)) {
        geo['polygon'] = coordsArrayToGeo(ca);
      }
    }
  }
  if (isSet(elevation)) {
    geo['elevation'] = elevation;
  }
  return geo;
}

const matchWhitespace = /\s+/

export function elevationToWkt(elevation) {
  let unit;
  [ elevation, unit ] = String(elevation).replaceAll(',', '').split(matchWhitespace);
  return +elevation;
}

export function coordsPointToWkt([ lng, lat ], elevation = null) {
  const elevationValue = isSet(elevation) ? ` ${elevationToWkt(elevation)}` : '';
  return `${lng} ${lat}${elevationValue}`;
}

export function coordsArrayToWkt(coordsArray, elevation = null) {
  return coordsArray.map(coords => coordsPointToWkt(coords, elevation)).join(', ');
}

export function coordsToWkt(coords, elevation = null) {
  const coordsArray = coords?.coordinates;
  if (!coordsArray) {
    return;
  }
  const hasElevation = isSet(elevation);
  const elevationToken = hasElevation ? ' Z' : '';
  let type, wkt;
  if (isPointCoords(coords)) {
    type = 'Point';
    wkt = `POINT${elevationToken} (${coordsPointToWkt(coordsArray, elevation)})`;
  } else if (isLineCoords(coords)) {
    type = 'LineString';
    wkt = `LINESTRING${elevationToken} (${coordsArrayToWkt(coordsArray, elevation)})`;
  } else if (isPolygonCoords(coords)) {
    type = 'MultiPolygon';
    wkt = `POLYGON${elevationToken} ((${coordsArrayToWkt(coordsArray?.[0], elevation)}))`;
  }
  return {
    'gsp:hasGeometry': {
      '@type': `http://www.opengis.net/ont/sf#${type}`,
      'gsp:asWKT': {
        '@type': 'http://www.opengis.net/ont/geosparql#wktLiteral',
        '@value': wkt
      },
      'gsp:crs': { '@id': 'http://www.opengis.net/def/crs/OGC/1.3/CRS84' },
    },
  };
}

export function date(date) {
  return date ? formatDate(utcOffset(date), 'data') : '..';
}

export function spatialCoverage(location) {
  const coords = location && get(location, 'coordinates');
  if (!coords) {
    return {};
  }
  const geo = coordsToGeo(coords);
  return geo ? { 'spatialCoverage': { '@type': 'Place', 'geo': geo } } : {};
}
